/**
 * Readers for rows of the transactions API (Result.transactions[]). A row
 * has the same shape as booking-details' packageMain; for an enquiry most
 * booking fields (dates, fare, pax counts, partnerCreatedAt) are null.
 */

import {
  formatIsoDate,
  formatIsoDateTime,
  isPackageEnquiry,
  isPackageFailed,
  isPackagePending,
  packageStatusOf,
} from '../../utils/packageUtils';

/** The status tabs, in display order. `all` never hides a row. */
export const TRANSACTION_TABS = [
  { key: 'all', label: 'All' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'pending', label: 'Pending' },
  { key: 'enquiry', label: 'Enquiries' },
  { key: 'failed', label: 'Cancelled / Failed' },
];

/** Which tab a row belongs to. An unknown status counts as pending, never hidden. */
export const transactionGroup = (row) => {
  const status = packageStatusOf(row?.status);
  if (status === 'confirmed') return 'confirmed';
  if (isPackageEnquiry(status)) return 'enquiry';
  if (isPackageFailed(status)) return 'failed';
  if (isPackagePending(status)) return 'pending';
  return 'pending';
};

/** "ENQUIRY_SENT_SUCCESS" -> "Enquiry sent", "CONFIRMED" -> "Confirmed". */
export const transactionStatusLabel = (status) => {
  const s = packageStatusOf(status);
  if (!s) return 'Pending';
  if (s.startsWith('enquiry'))
    return s.includes('fail') ? 'Enquiry failed' : 'Enquiry sent';
  const words = s.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The lead passenger (or the first one) as { name, email, phone }. */
export const transactionLead = (row) => {
  const list = Array.isArray(row?.passengers) ? row.passengers : [];
  const p = list.find((x) => x?.isLead) || list[0] || {};
  return {
    name: [p.title, p.firstName, p.lastName].filter(Boolean).join(' '),
    email: p.email || '',
    phone: p.phone || '',
  };
};

/**
 * When it was booked. partnerCreatedAt is null for enquiries, so fall back
 * to the date inside the booking id (PKGB<yyyymmdd>…).
 */
export const transactionBookedOn = (row) => {
  if (row?.partnerCreatedAt) return formatIsoDateTime(row.partnerCreatedAt);
  const m = /^PKGB(\d{4})(\d{2})(\d{2})/.exec(String(row?.pkgBookingId || ''));
  return m ? formatIsoDate(`${m[1]}-${m[2]}-${m[3]}`) : '';
};

/** Sort key: the booking timestamp, else the id (it starts with the date). */
const sortKey = (row) =>
  String(row?.partnerCreatedAt || '') ||
  String(row?.pkgBookingId || '').replace(/^PKGB/, '');

/** Newest first. */
export const sortTransactions = (rows) =>
  [...rows].sort((a, b) => sortKey(b).localeCompare(sortKey(a)));

/** Free-text match on what an agent would type: ids, package, traveller, contact. */
export const matchesTransaction = (row, text) => {
  const q = String(text || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const lead = transactionLead(row);
  return [
    row.pkgBookingId,
    row.clientBookingId,
    row.bookingRef,
    row.packageName,
    row.fdId,
    row.joiningCityLabel,
    row.status,
    lead.name,
    lead.email,
    lead.phone,
  ].some((v) => v && String(v).toLowerCase().includes(q));
};
